/**
 * Iter145 — Bank-Match Approval-Queue-Kachel.
 *
 * Zeigt offene Bank-Match-Vorschläge (aus camt.053-Import oder Beleg-Upload).
 * Buchhalter kann per Klick akzeptieren, ablehnen oder umleiten.
 */
import React, { useCallback, useEffect, useState } from "react";
import { formatDate } from "@/lib/formatDate";
import { useCompany } from "@/context/CompanyContext";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  BadgeCheck, Ban, ArrowLeftRight, RefreshCw, Sparkles, ChevronRight,
} from "lucide-react";

const TYPE_LABEL = { receipt: "Beleg", invoice: "Rechnung", bill: "Kreditor" };

export default function BankMatchSuggestionsCard() {
  const { company } = useCompany();
  const companyId = company?._id || company?.id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(
        `/companies/${companyId}/match-suggestions?status=pending`
      );
      setItems(r.data.items || []);
    } catch (e) {
      // silent — dashboard karte darf nicht crashen
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const handle = async (id, action) => {
    setBusy(id);
    try {
      if (action === "accept") {
        await apiClient.post(`/match-suggestions/${id}/accept`);
        toast.success("Zuordnung übernommen");
      } else if (action === "reject") {
        await apiClient.post(`/match-suggestions/${id}/reject`);
        toast.success("Vorschlag abgelehnt");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setBusy(null);
    }
  };

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <Card
      className="p-5 border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-white"
      data-testid="bank-match-suggestions-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-indigo-600" />
          <span className="text-base font-semibold text-slate-900">
            KI-Bank-Zuordnungen
          </span>
          <Badge className="bg-indigo-600 hover:bg-indigo-700 text-white ml-1">
            {items.length}
          </Badge>
        </div>
        <Button
          variant="ghost" size="sm"
          onClick={load}
          data-testid="refresh-suggestions-btn"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
        {items.map((sug) => (
          <div
            key={sug.id}
            className="rounded-lg border border-slate-200 bg-white p-3"
            data-testid={`suggestion-${sug.id}`}
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase">
                <span>{TYPE_LABEL[sug.source_type] || sug.source_type}</span>
                <ChevronRight className="h-3 w-3" />
                <span>Bank</span>
              </div>
              <Badge
                variant="secondary"
                className={
                  sug.score >= 0.9
                    ? "bg-emerald-100 text-emerald-800"
                    : sug.score >= 0.8
                    ? "bg-amber-100 text-amber-800"
                    : "bg-slate-100 text-slate-700"
                }
              >
                {(sug.score * 100).toFixed(0)}%
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm mb-2">
              <div>
                <div className="text-[10px] uppercase text-slate-500 tracking-wide">Quelle</div>
                <div className="font-medium text-slate-900 truncate">
                  {sug.source_snapshot?.name || sug.source_id.slice(0, 8)}
                </div>
                <div className="text-xs text-slate-600">
                  {sug.source_snapshot?.amount?.toFixed?.(2)} · {formatDate(sug.source_snapshot?.date)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500 tracking-wide">Bank</div>
                <div className="font-medium text-slate-900 truncate">
                  {sug.je_snapshot?.description || sug.journal_entry_id.slice(0, 8)}
                </div>
                <div className="text-xs text-slate-600">
                  {sug.je_snapshot?.amount?.toFixed?.(2)} · {formatDate(sug.je_snapshot?.date)}
                </div>
              </div>
            </div>

            <div className="text-xs text-slate-500 mb-2 italic">{sug.reason}</div>

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs flex-1"
                onClick={() => handle(sug.id, "accept")}
                disabled={busy === sug.id}
                data-testid={`accept-${sug.id}`}
              >
                <BadgeCheck className="h-3.5 w-3.5 mr-1" />
                Akzeptieren
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => handle(sug.id, "reject")}
                disabled={busy === sug.id}
                data-testid={`reject-${sug.id}`}
              >
                <Ban className="h-3.5 w-3.5 mr-1" />
                Ablehnen
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => toast.info("Manuelles Umleiten: siehe Bankabgleich-Ansicht")}
                disabled={busy === sug.id}
                data-testid={`reassign-${sug.id}`}
              >
                <ArrowLeftRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
